import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import axios from 'axios';
import type {
  ICartItemRequest,
  IProductRequest,
  ICartRequest
} from '@/data/_autogen/IValveVista';
import CartService from '@/services/CartService';
import { useAuthStore } from '@/stores/authStore';

const STORAGE_KEY = 'valve_vista_shopping_cart';

export const useCartStore = defineStore('cart', () => {
  // State
  const cartItems = ref<ICartItemRequest[]>([]);
  const products = ref<Map<number, IProductRequest>>(new Map());
  const cartId = ref<number | null>(null);
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const lastSync = ref<Date | null>(null);

  // Getters
  const totalItems = computed(() => {
    return cartItems.value.reduce((sum, item) => sum + (item.quantity || 0), 0);
  });

  const itemCount = computed(() => {
    return cartItems.value.length;
  });

  const subtotal = computed(() => {
    return cartItems.value.reduce(
      (sum, item) => sum + (item.unitPrice || 0) * (item.quantity || 0),
      0
    );
  });

  const isProductInCart = computed(() => (id: number) => cartItems.value.some(i => i.id === id));

  const isEmpty = computed(() => {
    return cartItems.value.length === 0;
  });

  const getItemById = (productId: number): ICartItemRequest | undefined => {
    return cartItems.value.find((item) => item.productId === productId);
  };

  const getProductById = (productId: number): IProductRequest | undefined => {
    return products.value.get(productId);
  };

  const getItemQuantity = (productId: number): number => {
    const item = getItemById(productId);
    return item?.quantity || 0;
  };

  // Actions
  const addItem = async (product: IProductRequest, quantity = 1) => {
    try {
      if (!product.id) throw new Error('Product must have an id');
      isLoading.value = true;
      error.value = null;

      const authStore = useAuthStore();

      // Request guest token if no auth token exists and this is the first item
      if (!authStore.isAuthenticated && isEmpty.value) {
        if (authStore.shouldRequestGuestToken()) {
          try {
            await authStore.requestGuestToken();
          } catch (tokenErr) {
            console.error('Failed to request guest token:', tokenErr);
            // Continue anyway, let the API call fail with proper error
          }
        }
      }

      // Check if item already exists
      const existingItem = getItemById(product.id!);
      if (existingItem) {
        existingItem.quantity = (existingItem.quantity || 0) + quantity;
        await CartService.updateItemQuantity(existingItem);
      } else {
        // Create new cart item
        const newItem: ICartItemRequest = {
          cartId: cartId.value || undefined,
          productId: product.id,
          productName: product.displayName || undefined,
          quantity,
          unitPrice: product.price,
          requestType: 1, // Create
        };

        // Call API to add item
        const response = await CartService.addItemToCart(newItem);

        // Update cartId if it was null (first item)
        if (!cartId.value && response.data.cartId) {
          setCartId(response.data.cartId);
        }
        //update cartItemId if it was null (first item)
        if(response.data.id) {
          newItem.id = response.data.id;
          newItem.productName = response.data.productName;
          newItem.cartId = response.data.cartId;
        }

        cartItems.value.push(newItem);
      }
      syncToLocalStorage();

      // Load product details for display
      loadProductDetails(product);

    } catch (err) {
      error.value = 'Failed to add item to cart';
      console.error('Add to cart error:', err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const removeItem = async (productId: number) => {
    try {
      isLoading.value = true;
      error.value = null;

      const item = getItemById(productId);
      if (!item) {
        throw new Error('Item not found in cart');
      }

      // Call API to remove item
      await CartService.removeItemFromCart({
        id: item.id,
        cartId: cartId.value || undefined,
        productId,
      });

      // Remove from local state
      const index = cartItems.value.findIndex(
        (i) => i.productId === productId
      );
      if (index > -1) {
        cartItems.value.splice(index, 1);
      }

      syncToLocalStorage();
    } catch (err) {
      error.value = 'Failed to remove item from cart';
      console.error('Remove from cart error:', err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const updateQuantity = async (productId: number, quantity: number) => {
    try {
      isLoading.value = true;
      error.value = null;

      // If quantity is 0, remove the item
      if (quantity === 0) {
        await removeItem(productId);
        return;
      }

      const item = getItemById(productId);
      if (!item) {
        throw new Error('Item not found in cart');
      }

      // Update quantity locally
      item.quantity = quantity;
      await CartService.updateItemQuantity(item);

      syncToLocalStorage();
    } catch (err) {
      error.value = 'Failed to update quantity';
      console.error('Update quantity error:', err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const clearCart = async () => {
    try {
      isLoading.value = true;
      error.value = null;

      // Call API to remove each item
      const removePromises = cartItems.value.map((item) =>
        CartService.removeItemFromCart({
          id: item.id,
          cartId: cartId.value || undefined,
          productId: item.productId,
        })
      );

      await Promise.all(removePromises);

      // Clear local state
      cartItems.value = [];
      products.value.clear();
      syncToLocalStorage();
    } catch (err) {
      if(axios.isAxiosError(err) && err.response?.status === 401) {
        resetCart();
        error.value = 'Session Expired: resetting cart.';
      }
      else {
        error.value = 'Failed to clear cart';
        console.error('Clear cart error:', error);
        throw err;
      }
    } finally {
      isLoading.value = false;
    }
  };

  const setCartId = (id: number) => {
    cartId.value = id;
    syncToLocalStorage();
  };

  const loadProductDetails = (product: IProductRequest) => {
    if (product.id) {
      products.value.set(product.id, product);
    }
  };

  // Persistence
  const syncToLocalStorage = () => {
    const data: ICartRequest = {
      id: cartId.value || undefined,
      cartItems: cartItems.value,
      requestTypes: 1, // Default to Create
    };

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      lastSync.value = new Date();
    } catch (e) {
      console.error('Failed to sync cart to localStorage:', e);
    }
  };

  const loadFromLocalStorage = (): ICartRequest | null => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
      console.error('Failed to load cart from localStorage:', e);
      localStorage.removeItem(STORAGE_KEY);
    }
    return null;
  };

  const hydrate = () => {
    const stored = loadFromLocalStorage();
    if (stored) {
      cartId.value = stored.id || null;
      cartItems.value = stored.cartItems || [];
    }
  };

  const resetCart = () => {
    cartItems.value = [];
    products.value.clear();
    cartId.value = null;
    error.value = null;
    isLoading.value = false;
    lastSync.value = null;

    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      console.error('Failed to clear cart from localStorage:', e);
    }
  };

  const deleteFullCart = () => {
    // Completely clear all cart state and localStorage
    cartItems.value = [];
    products.value.clear();
    cartId.value = null;
    isLoading.value = false;
    error.value = null;
    lastSync.value = null;

    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      console.error('Failed to delete cart from localStorage:', e);
    }
  };

  return {
    // State
    cartItems,
    products,
    cartId,
    isLoading,
    error,
    lastSync,

    // Getters
    totalItems,
    itemCount,
    subtotal,
    isEmpty,
    isProductInCart,
    getItemById,
    getProductById,
    getItemQuantity,

    // Actions
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    setCartId,
    loadProductDetails,
    syncToLocalStorage,
    loadFromLocalStorage,
    hydrate,
    resetCart,
    deleteFullCart,
  };
});
